import { useEffect } from "react";

import { GetStaticProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { env } from "next-runtime-env";

import CreationFlow from "components/creationFlow/CreationFlow";
import { GenericError } from "components/errorBoundary/ErrorBoundary";
import {
  useGetCustomisation,
  useGetProduct,
  useGetSchema,
  useSetCustomisation,
} from "queries/product-queries";
import { GetCanvasStaticProps } from "types/queries";
import { SSR_CONFIGURATION_ERROR } from "utils/error-utils";
import { getLocaleFromMarketplace } from "utils/prismic-utils";
import {
  getCanvasStaticPaths,
  getCanvasStaticPropsFactory,
} from "utils/server-utils";

export default function Start({
  ssrError,
}: GetCanvasStaticProps<ReturnType<typeof getCanvasStaticPropsFactory>>) {
  const router = useRouter();

  const { data: product } = useGetProduct();
  const { error: schemaError, isLoading: schemaIsLoading } = useGetSchema();
  const { error: customisationError } = useGetCustomisation();

  const { mutate: setCustomisation } = useSetCustomisation();

  useEffect(() => {
    if (customisationError) {
      /**
       * If Creation Flow is visited without any `customisation`,
       * set a best-guess locale rather than blowing up.
       */
      setCustomisation({
        data: {
          locale: getLocaleFromMarketplace(router.query.marketplace as string),
          product_slug: product?.museSlug,
        },
      });
    }
  }, [
    customisationError,
    router.query.marketplace,
    setCustomisation,
    product?.museSlug,
  ]);

  if (ssrError) {
    return <GenericError error={Error(ssrError)} fullPage />;
  }

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  if (schemaError) {
    return <GenericError error={schemaError} fullPage />;
  }

  if (schemaIsLoading) {
    // TODO: loading state / skeleton
    return null;
  }

  return (
    <>
      <Head>
        <title>
          {product.content.product.muse_product_name
            ? `${product.content.product.muse_product_name} | Wonderbly`
            : `Create | Wonderbly`}
        </title>
      </Head>

      <CreationFlow />
    </>
  );
}

export const getStaticPaths = getCanvasStaticPaths;
export const getStaticProps: GetStaticProps = async (context) => {
  const result = await getCanvasStaticPropsFactory("personalisation")(context);

  // Redirect non-PDC books to website on prod
  if (!result.props.usesPdc && env("NEXT_PUBLIC_CANVAS_ENV") === "production") {
    return {
      redirect: {
        destination:
          (context.params!.marketplace === "us"
            ? `/${context.params!.marketplace}`
            : "") +
          "/personalized-products/" +
          context.params!.solidus_slug,
        permanent: false,
      },
    };
  }

  return result;
};
